import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { type ActiveCohortSession, getNextCircle } from '../../services/circles.service';
import { getEcho, isWsEnabled } from '../../services/echo';
import { useActiveCohortSessions } from '../../stores/active-cohort-sessions.store';
import { useAuthStore } from '../../stores/auth.store';
import { useCircleDiscoveryStore } from '../../stores/circle-discovery.store';
import type { NextCircleInfo } from '../../types';
import { fireAndForget } from '../../utils/fireAndForget';
import { Z_BANNER } from '../../utils/layers';
import { isLiveSessionPath } from '../../utils/liveSessionPath';
import { pickBannerSession } from './bannerSession';
import { useBelowHeaderHost } from './belowHeader';
import StartingSoonCard from './StartingSoonCard';

/**
 * Global banner that polls for circle status on ANY authenticated page.
 * Shows "Your circle is starting!" when a signed-up circle goes in_progress.
 *
 * Mounted in ProtectedRoute so it's always present for logged-in users.
 * Does its OWN polling — independent of the CircleDiscoveryStore which
 * only polls when OnDemandCard is mounted on the empathy page.
 */
export default function CircleStartBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const reduceMotion = useReducedMotion();

  const [circleInfo, setCircleInfo] = useState<NextCircleInfo | null>(null);
  const [activeCohortSession, setActiveCohortSession] = useState<ActiveCohortSession | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [cohortDismissed, setCohortDismissed] = useState(false);
  // Subscribe to store's soloFallbackTriggered — single source of truth
  const soloFallback = useCircleDiscoveryStore((s) => s.soloFallbackTriggered);
  const soloFallbackRef = useRef(false);
  useEffect(() => {
    soloFallbackRef.current = soloFallback;
  }, [soloFallback]);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasNavigatedRef = useRef(false);
  const isFetchingRef = useRef(false);
  const trackedCircleIdRef = useRef<number | null>(null);

  // Derive a stable boolean so the poll effect only restarts when eligibility changes,
  // not on every route change within /app.
  //
  // Suppress polling AND rendering while in an active session. The banner cannot
  // appear during a session anyway (both showCircleStarting and showSoloFallback
  // require !isInSession below), so the poll data would just be thrown away —
  // and in the "signed up, circle imminent" tier that's ~20 req/min of wasted
  // traffic against the circles throttle group. The useEffect re-runs when
  // shouldPoll flips back to true on session exit and immediately calls poll(),
  // so the banner is fresh within ~100ms of exit with no separate mechanism.
  // The dev Circle Test Room counts too: the "Your circle is starting!"
  // banner is just noise/overlap there. One definition, shared with the
  // in-app ring (ListenerRingHost), since both subscribe to the same poll.
  const isInSession = isLiveSessionPath(location.pathname);
  const shouldPoll = isAuthenticated && !isInSession;

  // Poll for next circle status — only when signed up and circle is imminent
  useEffect(() => {
    if (!shouldPoll) return;

    let cancelled = false;
    let currentDelay = 10_000; // Start with 10s — adjusts based on proximity after first poll

    const poll = async () => {
      if (cancelled || isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const data = await getNextCircle();
        if (cancelled) return;

        // 2026-04-10 cleanup: removed the "tracked circle ID changed → trigger
        // soloFallback" heuristic. The comment that USED to be here said the
        // heuristic was supposed to suppress when "the user recently completed/
        // left a circle" — but the implementation never actually checked that.
        // After a successful session, the tracked circle DOES change (backend
        // returns next-hour's circle), and the heuristic fired a false positive.
        // Backend's solo_fallback_available is now the only source of truth.

        // Track which circle the user is signed up for (still useful for the
        // poll-interval-tier logic at the bottom of this function).
        if (data.is_signed_up) {
          trackedCircleIdRef.current = data.circle_id;
        } else if (!soloFallbackRef.current) {
          trackedCircleIdRef.current = null;
        }

        setCircleInfo(data);

        // Backend returns solo_fallback_available only for unclaimed fallbacks
        if (
          data.solo_fallback_available &&
          !soloFallbackRef.current &&
          !useCircleDiscoveryStore.getState().soloFallbackDismissed
        ) {
          useCircleDiscoveryStore.setState({ soloFallbackTriggered: true, signedUpCircleId: null });
        }

        // Adjust polling speed based on context
        if (data.is_signed_up && data.minutes_until <= 0.5) {
          currentDelay = 3_000;
        } else if (data.is_signed_up && data.minutes_until <= 5) {
          currentDelay = 10_000;
        } else if (data.is_signed_up) {
          currentDelay = 30_000;
        } else if (trackedCircleIdRef.current || data.solo_fallback_available) {
          // Was tracking a signup OR fallback available — poll fast to detect changes
          currentDelay = 5_000;
        } else {
          currentDelay = 60_000;
        }
      } catch {
        // Silently ignore — non-critical background poll
      } finally {
        isFetchingRef.current = false;
      }
      if (!cancelled) {
        pollRef.current = setTimeout(fireAndForget(poll), currentDelay);
      }
    };

    void poll();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        cancelled = true;
        if (pollRef.current) {
          clearTimeout(pollRef.current);
          pollRef.current = null;
        }
      } else {
        cancelled = false;
        void poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (pollRef.current) clearTimeout(pollRef.current);
      pollRef.current = null;
      isFetchingRef.current = false;
    };
  }, [shouldPoll]);

  // Active cohort sessions come from the SHARED poll, not a private one. This
  // banner is mounted globally and "Your Groups" renders inside it, so two
  // independent 30s timers were hitting the same endpoint for the same data —
  // and could disagree for a full interval.
  // shouldPoll, not an unconditional subscribe: the banner cannot render during
  // a live session (both showCircleStarting and showSoloFallback require
  // !isInSession), so polling there is traffic for data that gets thrown away.
  // The globally-mounted /circles/next poll above is gated the same way.
  const cohortSessions = useActiveCohortSessions(shouldPoll);
  useEffect(() => {
    setActiveCohortSession(shouldPoll ? pickBannerSession(cohortSessions) : null);
  }, [shouldPoll, cohortSessions]);

  // WS: subscribe to circle discovery events for any tracked circle.
  // Uses the circle.signup.{id} channel when we know the circle ID,
  // and re-subscribes when the tracked circle changes.
  const [trackedCircleId, setTrackedCircleId] = useState<number | null>(null);

  // Sync circleInfo.is_signed_up → trackedCircleId so the WS effect below
  // re-runs (and tears down the old subscription) when the user leaves.
  //
  // V1 regression: before this else-branch, leaving a circle left
  // trackedCircleId holding the stale circle_id. The WS effect kept the
  // `circle.signup.{staleId}` subscription alive; Reverb would re-auth it
  // periodically and the backend's channel closure (which requires
  // CircleParticipant.left_at IS NULL) correctly returned 403. The red
  // "POST /broadcasting/auth 403" in DevTools was this stale subscription.
  //
  // Keep circleInfo!==null guard to avoid thrashing the subscription
  // during the initial load window when getNextCircle() hasn't resolved.
  useEffect(() => {
    if (circleInfo === null) return;
    if (circleInfo.is_signed_up && circleInfo.circle_id) {
      setTrackedCircleId(circleInfo.circle_id);
    } else {
      setTrackedCircleId(null);
    }
  }, [circleInfo]);

  useEffect(() => {
    if (!shouldPoll || !isWsEnabled() || !trackedCircleId) return;

    const channelName = `circle.signup.${trackedCircleId}`;
    try {
      getEcho()
        .private(channelName)
        .listen('.circle.discovery.changed', async () => {
          try {
            const data = await getNextCircle();
            setCircleInfo(data);

            // 2026-04-10 cleanup: removed the "tracked circle ID changed →
            // trigger soloFallback" heuristic. Backend's solo_fallback_available
            // (already handled by fetchNextCircle in the discovery store) is the
            // only source of truth. WS pushes still update the local circleInfo
            // but don't manually trigger the modal.
          } catch {
            /* non-critical */
          }
        });
    } catch {
      /* echo may not be initialized */
    }

    return () => {
      try {
        getEcho().leave(channelName);
      } catch {
        /* ignore */
      }
    };
  }, [shouldPoll, trackedCircleId]);

  // WS: listen on user channel for circle status changes (always authed, no timing issues)
  // See GiverFlow.tsx V5 comment — stopListening must pass the callback
  // reference or it removes every component's listener on the channel.
  const userId = useAuthStore((s) => s.user?.id);
  useEffect(() => {
    if (!shouldPoll || !isWsEnabled() || !userId) return;

    const channelName = `user.${userId}`;
    const refetch = async () => {
      try {
        const data = await getNextCircle();
        setCircleInfo(data);
      } catch {
        /* non-critical */
      }
    };
    const listener = (event: { role: string }) => {
      if (event.role === 'circle') void refetch();
    };

    try {
      getEcho().private(channelName).listen('.empathy.status.changed', listener);
    } catch {
      /* echo may not be initialized */
    }

    return () => {
      try {
        getEcho().private(channelName).stopListening('.empathy.status.changed', listener);
      } catch {}
    };
  }, [shouldPoll, userId]);

  // Reset dismissed and navigation guard when circle changes or leaving a session
  useEffect(() => {
    setDismissed(false);
    hasNavigatedRef.current = false;
  }, [circleInfo?.circle_id]);

  // Reset dismissed when navigating away from a session page
  useEffect(() => {
    if (!isInSession) {
      setDismissed(false);
      setCohortDismissed(false);
      hasNavigatedRef.current = false;
    }
  }, [isInSession]);

  // Banner intentionally shows during phase='waiting' too — it's a visible
  // invitation with "Later" dismissal, not a forced redirect. When a user
  // taps "Leave and come back later" in PreStartLobby, landing on
  // /app/empathy and seeing the banner is the promised "come back" path.
  // The aggressive pull-back (OnDemandCard auto-navigate) is still skipped
  // during waiting; this banner, being user-driven, stays.
  const showCircleStarting =
    !dismissed &&
    !isInSession &&
    circleInfo?.status === 'in_progress' &&
    circleInfo?.is_signed_up &&
    // Same rule as the cohort half of this banner: it ANNOUNCES, so it bows
    // out once you leave. The card on the hub is what persists.
    !circleInfo?.viewer_has_left;

  const showCohortSession =
    !cohortDismissed && !isInSession && !showCircleStarting && !!activeCohortSession;

  // Don't show solo fallback banner on empathy page — FallbackMenu modal handles it there
  const isOnEmpathy = location.pathname === '/app/empathy';
  const showSoloFallback =
    !dismissed && !isInSession && !isOnEmpathy && !showCohortSession && soloFallback;

  const shouldShow = showCircleStarting || showCohortSession || showSoloFallback;

  const handleJoin = () => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    setDismissed(true);
    if (pollRef.current) clearTimeout(pollRef.current);
    if (!circleInfo) return;
    void navigate(`/app/circles/${circleInfo.circle_id}`);
  };

  const handleJoinCohort = () => {
    if (hasNavigatedRef.current || !activeCohortSession) return;
    hasNavigatedRef.current = true;
    setCohortDismissed(true);
    void navigate(`/app/circles/${activeCohortSession.circle_id}`);
  };

  const handleSoloOptions = () => {
    // Just dismiss the banner and navigate — let the empathy page's FallbackMenu handle it
    setDismissed(true);
    void navigate('/app/empathy');
  };

  const handleDismiss = () => {
    setDismissed(true);
    useCircleDiscoveryStore.getState().dismissSoloFallback();
  };

  const dismissShown = showCohortSession ? () => setCohortDismissed(true) : handleDismiss;

  // Placement A, as the starting-soon card (#1157): just below the header,
  // resting under the safe area once it scrolls away; the top toasts stack
  // under whichever of the two is up (belowHeader.ts).
  const hostRefFor = useBelowHeaderHost(location.pathname);

  return (
    <>
      <AnimatePresence>
        {shouldShow ? (
          <motion.div
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
            ref={hostRefFor('start-banner')}
            data-testid="circle-notification"
            className="ee-session-start-host ee-session-start-host--below-header"
            style={{ zIndex: Z_BANNER }}
          >
            {/* Announced once, politely; never takes focus; Escape inside dismisses (C6). */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: Escape inside the card dismisses it; the card itself is not a control. */}
            <section
              role="status"
              aria-live="polite"
              onKeyDown={(e) => {
                if (e.key === 'Escape') dismissShown();
              }}
              className={`ee-card ee-card--pad-sm ee-session-start${
                showSoloFallback
                  ? ' ee-session-start--solo'
                  : showCohortSession
                    ? ' ee-session-start--cohort'
                    : ''
              }`}
            >
              <div className="ee-session-start__text">
                <p className="ee-session-start__title">
                  {showSoloFallback
                    ? "It's just you this round"
                    : showCohortSession
                      ? `${activeCohortSession!.cohort_name} is live!`
                      : 'Your circle is starting!'}
                </p>
                <p className="ee-session-start__body">
                  {showSoloFallback
                    ? 'Practice with an AI partner or try 1-on-1'
                    : showCohortSession
                      ? 'Tap to join your circle now'
                      : 'Join now to connect with your group'}
                </p>
              </div>
              <div className="ee-session-start__actions">
                <button
                  type="button"
                  onClick={dismissShown}
                  className="ee-button ee-button--ghost ee-button--sm ee-session-start__later"
                >
                  Later
                </button>
                <button
                  type="button"
                  onClick={
                    showSoloFallback
                      ? handleSoloOptions
                      : showCohortSession
                        ? handleJoinCohort
                        : handleJoin
                  }
                  className="ee-button ee-button--primary ee-button--sm"
                >
                  {showSoloFallback ? 'Options' : 'Enter'}
                </button>
              </div>
            </section>
          </motion.div>
        ) : null}
      </AnimatePresence>
      {/* N9: the lowest-priority variant; it waits while any of the above shows. */}
      <StartingSoonCard blocked={shouldShow || isInSession} pathname={location.pathname} />
    </>
  );
}
